/* global chrome */


import { EXTENSION_CONFIG } from '../config/extensionConfig'

const { EXTENSION_IDS, TIMEOUTS } = EXTENSION_CONFIG

let activeExtensionId = null

function hasChromeRuntime() {
	return (
		typeof chrome !== 'undefined' &&
		chrome.runtime &&
		typeof chrome.runtime.sendMessage === 'function'
	)
}

function isNotInstalledError(err) {
	if (!err) return false
	const msg = String(err.message || err)
	return (
		msg.includes('Could not establish connection') ||
		msg.includes('Receiving end does not exist')
	)
}

function sendToOne(extensionId, action, data, timeoutMs) {
	return new Promise((resolve, reject) => {
		let settled = false

		const timer = setTimeout(() => {
			if (settled) return
			settled = true
			const err = new Error('Extension request timed out')
			err.isTimeout = true
			reject(err)
		}, timeoutMs)

		try {
			chrome.runtime.sendMessage(
				extensionId,
				{ type: 'FROM_WEBSITE', action, data },
				response => {
					if (settled) return
					clearTimeout(timer)
					settled = true

					if (chrome.runtime.lastError) {
						const err = new Error(chrome.runtime.lastError.message)
						err.isNotInstalled = isNotInstalledError(chrome.runtime.lastError)
						reject(err)
						return
					}

					if (response && response.error) {
						reject(new Error(response.error))
						return
					}

					resolve(response)
				}
			)
		} catch (err) {
			if (settled) return
			clearTimeout(timer)
			settled = true
			reject(err)
		}
	})
}

// Call an extension action. Returns the extension's response payload.
// Rejects with err.isTimeout / err.isNotInstalled / err.isUnsupported when
// the failure mode is identifiable.
export async function callExtension(
	action,
	data = {},
	timeoutMs = TIMEOUTS.DEFAULT
) {
	if (!hasChromeRuntime()) {
		const err = new Error('Browser does not support Chrome extensions')
		err.isUnsupported = true
		throw err
	}

	if (activeExtensionId) {
		return sendToOne(activeExtensionId, action, data, timeoutMs)
	}

	let lastError = null
	for (const id of EXTENSION_IDS) {
		try {
			const response = await sendToOne(id, action, data, timeoutMs)
			activeExtensionId = id
			return response
		} catch (err) {
			lastError = err
			// Try the next ID on any failure — could be wrong ID, not installed,
			// or unresponsive. Whichever ID answers becomes sticky.
		}
	}

	throw lastError || new Error('No extension responded')
}

// Probe the extension and classify the result. Returns one of:
//   { installed: true, compatible: true, version }
//   { installed: true, compatible: false, action: 'update', message }
//   { installed: false, compatible: false, action: 'install', message }
export async function detectExtension() {
	if (typeof chrome === 'undefined') {
		return {
			installed: false,
			compatible: false,
			action: 'install',
			message: 'Use Chrome to install the LoadLine extension.',
		}
	}

	if (!hasChromeRuntime()) {
		return {
			installed: false,
			compatible: false,
			action: 'install',
			message: 'LoadLine extension is not installed.',
		}
	}

	let installedButUnresponsive = false

	for (const id of EXTENSION_IDS) {
		try {
			const response = await sendToOne(id, 'pingBoard', {}, TIMEOUTS.PING)
			activeExtensionId = id
			return {
				installed: true,
				compatible: true,
				version: response?.version,
			}
		} catch (err) {
			if (err.isTimeout) {
				// Extension is reachable but didn't reply to a known action — old build.
				installedButUnresponsive = true
				activeExtensionId = id
			}
			// isNotInstalled or other → keep trying the next ID
		}
	}

	if (installedButUnresponsive) {
		return {
			installed: true,
			compatible: false,
			action: 'update',
			message: 'LoadLine extension is outdated. Please update it.',
		}
	}

	return {
		installed: false,
		compatible: false,
		action: 'install',
		message: 'LoadLine extension is not installed.',
	}
}

export const STORE_URL = EXTENSION_CONFIG.STORE_URL
